import React, { useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  Fab,
  FormControlLabel,
  FormGroup,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const SQUARE_FOOTAGE_FILTER_KEY = "__squareFootageTotal";

function isSquareFootageFieldType(type) {
  return String(type || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "") === "squarefootage";
}

function isBooleanFieldType(type) {
  return String(type || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "") === "boolean";
}

function Filters(props) {
  const {
    sizing,
    filterDrawerOpen,
    setFilterDrawerOpen,
    filtersRefreshing,
    clearAllFilters,
    allFilterCatalog,
    availableFilters,
    filterState,
    activeNumberSliderFieldId,
    setActiveNumberSliderFieldId,
    parseOptionalNumber,
    hasAnyFieldFilter,
    resetSingleFieldFilter,
    setNumberFieldFromSlider,
    commitNumberFilterRange,
    onNumberFilterDraftChange,
    normalizeNumberDraftForBounds,
    isNumberFilterInvalid,
    commitNumberFilter,
    setSquareFootageIncludeBasement,
    toggleStringFieldValue,
  } = props;

  const availableFieldMap = useMemo(
    () =>
      new Map(
        (availableFilters.fields || []).map((field) => [
          String(field.fieldId),
          field,
        ])
      ),
    [availableFilters.fields]
  );

  const orderedFilterFields = useMemo(() => {
    const fields = Array.isArray(allFilterCatalog.fields)
      ? [...allFilterCatalog.fields]
      : [];
    const squareIndex = fields.findIndex(
      (field) => String(field?.fieldId) === SQUARE_FOOTAGE_FILTER_KEY
    );

    if (squareIndex > 0) {
      const [squareField] = fields.splice(squareIndex, 1);
      fields.unshift(squareField);
    }

    return fields;
  }, [allFilterCatalog.fields]);

  const featureFields = useMemo(
    () => orderedFilterFields.filter((field) => isBooleanFieldType(field?.type)),
    [orderedFilterFields]
  );

  const standardFilterFields = useMemo(
    () => orderedFilterFields.filter((field) => !isBooleanFieldType(field?.type)),
    [orderedFilterFields]
  );

  const drawerTop = {
    xs: `${Math.max(
      0,
      (sizing?.xs?.usedHeight ?? 0) - (sizing?.xs?.footer ?? 0)
    )}px`,
    sm: `${Math.max(
      0,
      (sizing?.sm?.usedHeight ?? 0) - (sizing?.sm?.footer ?? 0)
    )}px`,
  };

  const drawerBottom = {
    xs: `${sizing?.xs?.footer ?? 0}px`,
    sm: `${sizing?.sm?.footer ?? 0}px`,
  };
  const drawerCenterTop = {
    xs: `calc((${drawerTop.xs} + (100vh - ${drawerBottom.xs})) / 2)`,
    sm: `calc((${drawerTop.sm} + (100vh - ${drawerBottom.sm})) / 2)`,
  };

  return (
    <>
      <Fab
        size="small"
        color="primary"
        aria-label="filter"
        className="browse-designs-filter-fab"
        onClick={() => setFilterDrawerOpen(true)}
      >
        <TuneIcon fontSize="small" />
      </Fab>

      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: { xs: "88vw", sm: 380 },
              p: 2,
              backgroundColor: "#f6f7f9",
              top: drawerTop,
              bottom: drawerBottom,
              height: "auto",
              maxHeight: "none",
              boxSizing: "border-box",
              overflowX: "visible",
              overflowY: "hidden",
              padding: "0 0 0 16px",
            },
          },
        }}
      >
        <Fab
          size="small"
          onClick={() => setFilterDrawerOpen(false)}
          sx={{
            position: "fixed",
            right: { xs: "calc(88vw - 18px)", sm: "calc(380px - 18px)" },
            top: drawerCenterTop,
            transform: "translateY(-50%)",
            border: "1px solid #9aa8bb",
            backgroundColor: "#e7ecf3",
            color: "#3f4e63",
            width: 36,
            height: 36,
            minHeight: 36,
            boxShadow: 2,
            zIndex: 1400,
            "&:hover": {
              backgroundColor: "#dce3ed",
            },
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </Fab>

        <Box sx={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pl: 4.5,
              marginTop: "16px",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1.25,
                py: 0.5,
                border: "1px solid #9aa8bb",
                backgroundColor: "#e7ecf3",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: '"Alegreya Sans SC", "Trebuchet MS", sans-serif',
                  letterSpacing: "1.8px",
                  textTransform: "uppercase",
                  color: "#3f4e63",
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                Filters
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={clearAllFilters}
              disabled={filtersRefreshing}
            >
              Clear
            </Button>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {standardFilterFields.map((field) => {
            if (isSquareFootageFieldType(field?.type)) {
              return null;
            }

            const fieldId = String(field.fieldId);
            const selectedConfig = filterState.fields?.[fieldId] || {
              values: [],
              min: "",
              max: "",
            };
            const isFieldAvailable = availableFieldMap.has(fieldId);
            const hasActiveFilter = hasAnyFieldFilter(selectedConfig);
            const fieldDisabled =
              filtersRefreshing || (!isFieldAvailable && !hasActiveFilter);
            const availableField = availableFieldMap.get(fieldId);
            const isSquareFootageAggregateFilter =
              fieldId === SQUARE_FOOTAGE_FILTER_KEY;

            if (field.type === "number") {
              const sliderMinRaw =
                availableField?.min !== undefined &&
                availableField?.min !== null
                  ? Number(availableField.min)
                  : Number(field.min);
              const sliderMaxRaw =
                availableField?.max !== undefined &&
                availableField?.max !== null
                  ? Number(availableField.max)
                  : Number(field.max);
              const sliderMin = Number.isFinite(sliderMinRaw)
                ? sliderMinRaw
                : 0;
              const sliderMax = Number.isFinite(sliderMaxRaw)
                ? sliderMaxRaw
                : sliderMin;
              const selectedMinRaw = parseOptionalNumber(selectedConfig.min);
              const selectedMaxRaw = parseOptionalNumber(selectedConfig.max);
              const safeSelectedMin =
                selectedMinRaw !== null
                  ? Math.max(sliderMin, Math.min(selectedMinRaw, sliderMax))
                  : sliderMin;
              const safeSelectedMax =
                selectedMaxRaw !== null
                  ? Math.max(
                      safeSelectedMin,
                      Math.min(selectedMaxRaw, sliderMax)
                    )
                  : sliderMax;
              const isEqualRange = safeSelectedMin === safeSelectedMax;
              const isDraggingThisSlider = activeNumberSliderFieldId === fieldId;
              const numberFieldDisabled =
                fieldDisabled || (isEqualRange && !isDraggingThisSlider);

              return (
                <Box key={`field-filter-${fieldId}`} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      mb: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography variant="subtitle2">
                      {field.name} ({safeSelectedMin} - {safeSelectedMax})
                    </Typography>
                    <Button
                      size="small"
                      disabled={filtersRefreshing || !hasActiveFilter}
                      onClick={() => resetSingleFieldFilter(fieldId)}
                    >
                      Reset
                    </Button>
                  </Box>
                  <Slider
                    value={[safeSelectedMin, safeSelectedMax]}
                    min={sliderMin}
                    max={sliderMax}
                    step={1}
                    sx={{
                      width: "calc(100% - 44px)",
                      mx: "22px",
                      "& .MuiSlider-thumb": {
                        width: 18,
                        height: 18,
                      },
                      ...(isEqualRange
                        ? {
                            '& .MuiSlider-thumb[data-index="0"]': {
                              left: "0% !important",
                            },
                            '& .MuiSlider-thumb[data-index="1"]': {
                              left: "100% !important",
                            },
                          }
                        : {}),
                      "& .MuiSlider-rail": {
                        height: 5,
                      },
                      "& .MuiSlider-track": {
                        height: 5,
                        ...(isEqualRange
                          ? {
                              left: "0% !important",
                              width: "100% !important",
                            }
                          : {}),
                      },
                    }}
                    disabled={numberFieldDisabled}
                    onChange={(event, value) => {
                      if (!Array.isArray(value)) return;
                      setActiveNumberSliderFieldId(fieldId);
                      setNumberFieldFromSlider(fieldId, value[0], value[1]);
                    }}
                    onChangeCommitted={(event, value) => {
                      if (!Array.isArray(value)) return;
                      setActiveNumberSliderFieldId(null);
                      commitNumberFilterRange(fieldId, value[0], value[1]);
                    }}
                    valueLabelDisplay="auto"
                  />
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{ mt: 0.5 }}
                  >
                    <TextField
                      type="number"
                      size="small"
                      label="Min"
                      fullWidth
                      value={
                        selectedConfig.min === ""
                          ? String(safeSelectedMin)
                          : selectedConfig.min
                      }
                      disabled={numberFieldDisabled}
                      onChange={(e) =>
                        onNumberFilterDraftChange(fieldId, "min", e.target.value)
                      }
                      onBlur={() =>
                        normalizeNumberDraftForBounds(fieldId, sliderMin, sliderMax)
                      }
                    />
                    <TextField
                      type="number"
                      size="small"
                      label="Max"
                      fullWidth
                      value={
                        selectedConfig.max === ""
                          ? String(safeSelectedMax)
                          : selectedConfig.max
                      }
                      disabled={numberFieldDisabled}
                      onChange={(e) =>
                        onNumberFilterDraftChange(fieldId, "max", e.target.value)
                      }
                      onBlur={() =>
                        normalizeNumberDraftForBounds(fieldId, sliderMin, sliderMax)
                      }
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: { xs: "100%", sm: 84 } }}
                      disabled={numberFieldDisabled || isNumberFilterInvalid(fieldId)}
                      onClick={() =>
                        commitNumberFilter(
                          fieldId,
                          sliderMin,
                          sliderMax,
                          safeSelectedMin,
                          safeSelectedMax
                        )
                      }
                    >
                      Apply
                    </Button>
                  </Stack>
                  {isSquareFootageAggregateFilter && (
                    <Box
                      sx={{
                        mt: 0.75,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "#5f6f86", whiteSpace: "nowrap" }}
                      >
                        Include Basement
                      </Typography>
                      <Switch
                        size="small"
                        checked={Boolean(filterState?.squareFootage?.includeBasement)}
                        disabled={numberFieldDisabled}
                        onChange={(event) =>
                          setSquareFootageIncludeBasement(event.target.checked)
                        }
                      />
                    </Box>
                  )}
                </Box>
              );
            }

            const allValues = Array.isArray(field.values) ? field.values : [];
            const availableValues = new Set(
              Array.isArray(availableField?.values) ? availableField.values : []
            );

            return (
              <Box key={`field-filter-${fieldId}`} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    mb: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle2">{field.name}</Typography>
                  <Button
                    size="small"
                    disabled={filtersRefreshing || !hasActiveFilter}
                    onClick={() => resetSingleFieldFilter(fieldId)}
                  >
                    Reset
                  </Button>
                </Box>
                <Box sx={{ maxHeight: 220, overflowY: "auto", pr: 1 }}>
                  <FormGroup>
                    {allValues.map((value) => {
                      const selected = (selectedConfig.values || []).includes(
                        value
                      );
                      const optionAvailable = availableValues.has(value);
                      const disabled =
                        filtersRefreshing || (!selected && !optionAvailable);

                      return (
                        <FormControlLabel
                          key={`${fieldId}-${value}`}
                          control={
                            <Checkbox
                              checked={selected}
                              disabled={disabled}
                              onChange={() =>
                                toggleStringFieldValue(fieldId, value)
                              }
                            />
                          }
                          label={value}
                        />
                      );
                    })}
                  </FormGroup>
                </Box>
              </Box>
            );
          })}

          {featureFields.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Features
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {featureFields.map((field) => {
                  const fieldId = String(field.fieldId);
                  const selectedConfig = filterState.fields?.[fieldId] || {
                    values: [],
                    min: "",
                    max: "",
                  };
                  const isSelected = (selectedConfig.values || []).includes("true");
                  const availableField = availableFieldMap.get(fieldId);
                  const availableValues = Array.isArray(availableField?.values)
                    ? availableField.values.map((value) => String(value).toLowerCase())
                    : [];
                  const isAvailable = availableValues.includes("true");
                  const disabled =
                    filtersRefreshing || (!isSelected && !isAvailable);

                  return (
                    <Button
                      key={`feature-filter-${fieldId}`}
                      variant={isSelected ? "contained" : "outlined"}
                      size="small"
                      disabled={disabled}
                      onClick={() => toggleStringFieldValue(fieldId, "true")}
                      sx={{
                        borderRadius: 999,
                        textTransform: "none",
                        ...(isSelected
                          ? {
                              backgroundColor: "#5f738f",
                              "&:hover": {
                                backgroundColor: "#4d6079",
                              },
                            }
                          : {
                              borderColor: "#9aa8bb",
                              color: "#3f4e63",
                            }),
                      }}
                    >
                      {field.name}
                    </Button>
                  );
                })}
              </Box>
            </Box>
          )}

          {filtersRefreshing && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Updating available filters...
              </Typography>
            </Stack>
          )}
        </Box>
      </Drawer>
    </>
  );
}

export default Filters;
